import { push } from "connected-react-router";
import { defineMessages } from "react-intl";

import { EntityType } from "@mapmycustomers/shared";
import FilterOperator from "@mapmycustomers/shared/enum/FilterOperator";

import TourDescriptor from "@app/component/flows/types/TourDescriptor";
import i18nService from "@app/config/I18nService";
import localSettings from "@app/config/LocalSettings";
import Path from "@app/enum/Path";
import { applyListViewSettings } from "@app/scene/company/store/actions";
import { getListViewState } from "@app/scene/company/store/selectors";
import { getApiService } from "@app/store/api";
import { getCurrentUserId, getOrganizationId } from "@app/store/iam";
import { selectSavedFilter } from "@app/store/savedFilters/actions";
import CommonFieldName from "@app/util/fieldModel/CommonFieldName";
import companyFieldModel from "@app/util/fieldModel/CompanyFieldModel";
import { createAndCondition, createSimpleCondition } from "@app/util/filters/utils";
import { convertToPlatformFilterModel } from "@app/util/viewModel/convertToPlatformFilterModel";

const messages = defineMessages({
  step1: {
    id: "guides.frequencies.tour.step1",
    defaultMessage: "Record without frequency",
  },
  step1Description: {
    id: "guides.frequencies.tour.step1.description",
    defaultMessage:
      "Here's a record without a frequency. Let's check this out. Hover over the row and click ‘View’ to proceed.",
  },
  step2: {
    id: "guides.frequencies.tour.step2",
    defaultMessage: "Frequencies",
  },
  step2Description: {
    id: "guides.frequencies.tour.step2.description",
    defaultMessage: "Hover over the help text to learn more about frequencies.",
  },
  step3: {
    id: "guides.frequencies.tour.step3",
    defaultMessage: "Frequency Interval",
  },
  step3Description: {
    id: "guides.frequencies.tour.step3.description",
    defaultMessage: "Please set frequency interval to proceed.",
  },
  step4: {
    id: "guides.frequencies.tour.step4",
    defaultMessage: "Schedule Activity",
  },
  step4Description: {
    id: "guides.frequencies.tour.step4.description",
    defaultMessage:
      "Please note, the record will not have a status until the first activity is logged on the record.",
  },
});

const errorMessages = defineMessages({
  noRecords: {
    id: "guides.frequencies.tour.error.noRecords",
    defaultMessage:
      "This tour requires companies, but you don't have any. Feel free to create one and then re-start this tour again.",
  },
  noSuitableRecords: {
    id: "guides.frequencies.tour.error.noSuitableRecords",
    defaultMessage:
      "This tour requires a company that you own, has no frequency interval, and isn't part of any group. It looks like you don't have one yet — create a matching company and restart the tour.",
  },
});

export const frequenciesTourConfiguration: TourDescriptor = {
  id: "mmc-frequencies",
  steps: [
    {
      id: "record",
      description: messages.step1Description,
      onBefore: async ({ store }) => {
        const currentUserId = getCurrentUserId(store.getState());
        const filterModel = createAndCondition([
          createSimpleCondition(CommonFieldName.USER, FilterOperator.IN_ANY, [currentUserId]),
          createSimpleCondition(CommonFieldName.FREQUENCY_INTERVAL, FilterOperator.EMPTY),
          createSimpleCondition(CommonFieldName.GROUPS, FilterOperator.EMPTY),
        ]);

        const apiService = getApiService(store.getState());
        const orgId = getOrganizationId(store.getState());
        const response = await apiService!.fetchPins(orgId!, {
          $filters: {
            entities: {
              [EntityType.COMPANY]: convertToPlatformFilterModel(
                filterModel,
                [],
                companyFieldModel
              ),
            },
          },
          $limit: 0,
        });

        if (response.accessible === 0) {
          // show "no records" message
          return { action: "error", message: i18nService.formatMessage(errorMessages.noRecords) };
        }

        if (response.total === 0) {
          // show "no records without frequencies" message
          return {
            action: "error",
            message: i18nService.formatMessage(errorMessages.noSuitableRecords),
          };
        }

        // keeping selected page size
        const { range } = getListViewState(store.getState());
        const pageSize = range.endRow - range.startRow;

        // ensure the saved filter is not selected, otherwise it will re-write out filterModel
        store.dispatch(
          selectSavedFilter({
            entityType: EntityType.COMPANY,
            savedFilter: undefined,
            skipUrlUpdate: true,
          })
        );

        store.dispatch(
          applyListViewSettings({
            filter: filterModel,
            range: { endRow: pageSize, startRow: 0 },
            selectedSavedFilterId: undefined,
          })
        );

        // let's ensure frequency panel will be open
        localSettings.setRecordPaneFrequencyOpenState(true);

        // We do know our store accepts connected-react-router's actions
        // @ts-ignore
        store.dispatch(push(Path.COMPANY));
      },
      options: {
        disableInteraction: false,
        expectRouteChange: true,
        showButtons: false,
      },
      position: "bottom",
      selector: ".ag-row-first",
      title: messages.step1,
      waitForEvent: {
        eventType: "internal",
        selector: "Clicked: Company Table -> View",
      },
    },
    {
      id: "frequencies-info",
      description: messages.step2Description,
      options: {
        disableInteraction: false,
        showBackground: false,
      },
      position: "left",
      selector: "[class*=FrequencyBox_container] [class*=HelpText_container]",
      title: messages.step2,
    },
    {
      id: "frequency-selector",
      description: messages.step3Description,
      options: {
        disableInteraction: false,
        showBackground: false,
        showButtons: false,
      },
      position: "left",
      selector: "[class*=FrequencyBox_container] [class*=FrequencySelector_container]",
      title: messages.step3,
      waitForEvent: {
        eventType: "internal",
        selector: "Clicked: Company Record Pane -> Set a Frequency -> Selected Interval",
      },
    },
    {
      id: "scheduling",
      description: messages.step4Description,
      position: "left",
      selector: "[class*=FrequencyBox_container] [class*=NextActivityState_container]",
      title: messages.step4,
    },
  ],
};
